import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { CfPopoverBase } from 'components/popover-host/popover-interface';
import { PopoverController } from 'providers/popover-controller';
import {
  AppInstallService,
  AppInstallTriggerSource,
  InstallTarget,
  InstallTargetKind,
  VisibleInstallTarget,
} from 'services/app-install.service';
import { LL } from 'i18n/i18n.service';

// Sheet only renders for the two visible-install kinds — None / PwaPrompt
// don't have a sheet UI and would silently emit misleading telemetry if
// passed in. The static present() helper guards at the entry; the type
// narrows the rest of the component's contract.
export interface CsAppInstallSheetParams {
  target: VisibleInstallTarget;
  triggerSource: AppInstallTriggerSource;
}

// Bottom-sheet popover for promoting the native iOS / Android apps to
// mobile-web users (and the "Open in app" affordance when detection says the
// app is already installed). Rendered identically on startup (cold-start
// nudge) and on Settings → Install App on mobile — the trigger_source
// telemetry prop carries the surface so we can attribute installs.
//
// Navigation is anchor-driven so the browser hands the link to the OS:
// cs-store-badges renders <a target="_blank"> internally, and the
// "Open in app" CTA is itself a plain <a> styled to look like a primary
// cs-button (HTML5 forbids nesting <button> inside <a>, so we can't wrap a
// cs-button — we restyle the anchor instead). The component's click handlers
// fire telemetry + dismiss only — no window.open, no double-fire.
//
// That "Open in app" CTA is rendered on Android only. iOS gets no such link
// (#5660) — see design-docs/install-affordances.md, "Why iOS has no
// Open-in-app link".
//
// The popover host (popover-host.ts) provides drag handle, swipe-to-dismiss,
// backdrop, slide animation, and blur — this component only renders content.
@Component({
  selector: 'cs-app-install-sheet',
  templateUrl: 'cs-app-install-sheet.html',
  styleUrls: ['cs-app-install-sheet.scss'],
  host: { 'class': 'app-install-sheet' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsAppInstallSheet extends CfPopoverBase implements OnInit {
  protected LL = LL;
  protected InstallTargetKind = InstallTargetKind;

  @Input() params!: CsAppInstallSheetParams;

  get target(): VisibleInstallTarget { return this.params.target; }

  // bound by template — undefined for NativeOpen so cs-store-badges renders empty
  get iosInstallUrl(): string | undefined {
    return this.target.kind === InstallTargetKind.NativeStore && this.target.platform === 'ios'
      ? this.target.storeUrl
      : undefined;
  }

  get androidInstallUrl(): string | undefined {
    return this.target.kind === InstallTargetKind.NativeStore && this.target.platform === 'android'
      ? this.target.storeUrl
      : undefined;
  }

  // href for the open-in-app anchor — the App Link URL on NativeOpen, and on
  // NativeStore only when the target carries one (Android does; iOS
  // deliberately does not since #5660). Empty string otherwise: the template
  // renders the anchor only where an openUrl exists.
  get openCtaHref(): string {
    if (this.target.kind === InstallTargetKind.NativeStore || this.target.kind === InstallTargetKind.NativeOpen) {
      return this.target.openUrl ?? '';
    }
    return '';
  }

  static async present(
    popoverCtrl: PopoverController,
    target: InstallTarget,
    triggerSource: AppInstallTriggerSource,
  ): Promise<void> {
    if (target.kind !== InstallTargetKind.NativeStore && target.kind !== InstallTargetKind.NativeOpen) {
      // defense in depth — callers check shouldShowStartupSheet / resolve first,
      // so reaching this branch means the caller bypassed the gate
      return;
    }
    await popoverCtrl.present(
      CsAppInstallSheet,
      { params: { target, triggerSource } satisfies CsAppInstallSheetParams },
      {
        animate: true,
        showBackdrop: true,
        bottomSheet: true,
        targetFromElement: document.body,
      }
    );
  }

  constructor(
    private popoverCtrl: PopoverController,
    private appInstallSvc: AppInstallService,
  ) {
    super();
  }

  ngOnInit(): void {
    // sheet-impression telemetry fires once per present() call — the popover
    // host instantiates exactly one component per call, and ngOnInit runs
    // after @Input bindings are resolved (unlike the constructor)
    this.appInstallSvc.trackAppInstallEvent('startup-sheet-impression', this.params.triggerSource, this.target, null);
  }

  onStoreCtaClicked(): void {
    // anchor inside cs-store-badges handles the navigation; we only emit
    // telemetry + dismiss
    this.appInstallSvc.trackAppInstallEvent('sheet-cta', this.params.triggerSource, this.target, 'store-badge');
    void this.popoverCtrl.dismiss(this);
  }

  onOpenCtaClicked(): void {
    // wrapping <a href target=_blank> handles the Universal Link navigation;
    // we only emit telemetry + dismiss
    this.appInstallSvc.trackAppInstallEvent('sheet-cta', this.params.triggerSource, this.target, 'open-link');
    void this.popoverCtrl.dismiss(this);
  }

  onContinueInBrowserClicked(): void {
    void this.popoverCtrl.dismiss(this);
  }

  // #5707: only the startup sheet offers the opt-out. Settings → Install App
  // opens the sheet on request, and is how an opted-out user can still install.
  get showDontShowAgain(): boolean {
    return this.params.triggerSource === 'startup';
  }

  // records this browser's opt-out, sends telemetry, and closes the sheet
  onDontShowAgainClicked(): void {
    this.appInstallSvc.trackAppInstallEvent('sheet-cta', this.params.triggerSource, this.target, 'dont-show-again');
    this.appInstallSvc.optOutOfStartupSheet();
    void this.popoverCtrl.dismiss(this);
  }

  onWillDismiss(_data: any): void {
    // no-op — telemetry fires from the CTA handlers
  }
}
